import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector }      from 'react-redux';
import { useTheme }                      from '@mui/material/styles';
import { CircularProgress }              from '@mui/material';

import { Get_one_Sales_Action, Update_one_Sales_Action } from '../../Redux/Actions/SalesAction';
import { GetCitiesData }                                  from '../../Redux/Actions/CityAction';
import { Getalldatauserlogsales }                         from '../../Redux/Actions/AuthAction';
import {
  Getall_data_user_log_Mangers,
  Getall_data_user_log_teamleaders,
} from '../../Redux/Actions/RoleData_Actions';
import { Notify }         from '../../Utility/Notify';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Constants ─────────────────────────────────────────────────── */
const AVATAR_COLORS = ['#4f46e5','#0891b2','#16a34a','#dc2626','#d97706','#7c3aed','#db2777'];
const getAvatarColor = str => AVATAR_COLORS[(str?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

/* ─── Icons ──────────────────────────────────────────────────────── */
const IconSearch  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconClose   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconCheck   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
const IconCity    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V21H3V9.5z"/><rect x="9" y="14" width="6" height="7" rx="1"/></svg>;
const IconUser    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconTeam    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconManager = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconNote    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>;
const IconEdit    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;

/* ─── Reusable SearchBox ─────────────────────────────────────────── */
const SearchBox = ({ value, onChange, placeholder, isDark, accentColor = '#0d2a6e' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: isDark ? '#151827' : '#f8fafc', border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, borderRadius: 8 }}>
    <span style={{ color: accentColor, opacity: 0.7, flexShrink: 0 }}><IconSearch /></span>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ border: 'none', background: 'none', outline: 'none', fontSize: 13, width: '100%', color: isDark ? '#e5e7eb' : '#374151', fontFamily: "'DM Sans',sans-serif" }} />
    {value && <button onClick={() => onChange('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: isDark ? '#7070a0' : '#9ca3af', display: 'flex' }}><IconClose /></button>}
  </div>
);

/* ─── SectionLabel ───────────────────────────────────────────────── */
const SectionLabel = ({ icon, label, required, color, isDark }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <span style={{ color, display: 'flex' }}>{icon}</span>
    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#9ca3af' : '#64748b' }}>
      {label} {required && <span style={{ color: '#E53935' }}>*</span>}
    </span>
  </div>
);

/* ─── Panel ──────────────────────────────────────────────────────── */
const Panel = ({ isDark, active, children }) => (
  <div style={{ border: `1.5px solid ${active ? '#0d2a6e' : (isDark ? '#2d3248' : '#e2e8f0')}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s', background: isDark ? '#1a1d2e' : '#fff', boxShadow: active ? '0 0 0 3px rgba(13,42,110,0.08)' : 'none' }}>
    {children}
  </div>
);

/* ─── Avatar ─────────────────────────────────────────────────────── */
const Av = ({ name, size = 36 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', background: getAvatarColor(name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.4, flexShrink: 0 }}>
    {name?.charAt(0)?.toUpperCase()}
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════ */
export const UpdateSales = () => {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const isDark      = useTheme().palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  /* form */
  const [formData, setFormData] = useState({ name: '', city: [], note: '', userlog: '', teamleader: '', manager: '' });
  const [loading, setLoading]   = useState(false);
  const [dataReady, setDataReady] = useState(false);

  /* search */
  const [citySearch, setCitySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [tlSearch,   setTlSearch]   = useState('');
  const [mgrSearch,  setMgrSearch]  = useState('');

  /* local data */
  const [cityData,       setCityData]       = useState([]);
  const [userLogData,    setUserLogData]     = useState([]);
  const [teamLeaderData, setTeamLeaderData]  = useState([]);
  const [managerData,    setManagerData]     = useState([]);

  /* fetch all */
  useEffect(() => {
    Promise.all([
      dispatch(Get_one_Sales_Action(id)),
      dispatch(GetCitiesData()),
      dispatch(Getalldatauserlogsales()),
      dispatch(Getall_data_user_log_teamleaders()),
      dispatch(Getall_data_user_log_Mangers()),
    ]);
  }, [dispatch, id]);

  /* redux selectors */
  const resSales       = useSelector(s => s.AllSales.salesupdate);
  const resCity        = useSelector(s => s.AllCities.city);
  const resUserlog     = useSelector(s => s.AuthRoot.Signup);
  const resTeamLeaders = useSelector(s => s.AllRoleData.RolesData);
  const resManagers    = useSelector(s => s.AllRoleData.salesformanagers);

  useEffect(() => { if (resCity?.data)        setCityData(resCity.data); },              [resCity]);
  useEffect(() => { if (resUserlog?.data)     setUserLogData(resUserlog.data); },         [resUserlog]);
  useEffect(() => { if (resTeamLeaders?.data) setTeamLeaderData(resTeamLeaders.data); }, [resTeamLeaders]);
  useEffect(() => { if (resManagers?.data)    setManagerData(resManagers.data); },       [resManagers]);

  /* populate form with existing sales data */
  useEffect(() => {
    const salesdata = resSales?.data;
    if (!salesdata) return;
    setFormData({
      name:       salesdata.name        || '',
      city:       Array.isArray(salesdata.city) ? salesdata.city.map(c => c._id || c) : [],
      note:       salesdata.notes       || '',
      userlog:    salesdata.userlog?._id || salesdata.userlog || '',
      teamleader: salesdata.teamleader?._id || salesdata.teamleader || '',
      manager:    salesdata.Manager?._id    || salesdata.Manager    || '',
    });
    setDataReady(true);
  }, [resSales]);

  /* derived */
  const filteredCities = cityData.filter(c => c.name?.toLowerCase().includes(citySearch.toLowerCase()));
  const filteredUsers  = userLogData.filter(u => u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()));
  const filteredTL     = teamLeaderData.filter(l => l.name?.toLowerCase().includes(tlSearch.toLowerCase()) || l.email?.toLowerCase().includes(tlSearch.toLowerCase()));
  const filteredMgr    = managerData.filter(m => m.name?.toLowerCase().includes(mgrSearch.toLowerCase()) || m.email?.toLowerCase().includes(mgrSearch.toLowerCase()));

  const selectedCities = cityData.filter(c => formData.city.includes(c._id));
  const selectedUser   = userLogData.find(u => u._id === formData.userlog);
  const selectedTL     = teamLeaderData.find(l => l._id === formData.teamleader);
  const selectedMgr    = managerData.find(m => m._id === formData.manager);

  const toggleCity = id => setFormData(p => ({ ...p, city: p.city.includes(id) ? p.city.filter(x => x !== id) : [...p.city, id] }));

  /* submit */
  const handleSubmit = async () => {
    if (!formData.name.trim())    { Notify('error', 'Name is required', 'error');               return; }
    if (!formData.note.trim())    { Notify('error', 'Note is required', 'error');               return; }
    if (!formData.city.length)    { Notify('error', 'Select at least one city', 'error');       return; }
    if (!formData.userlog)        { Notify('error', 'User log is required', 'error');            return; }
    setLoading(true);
    try {
      await dispatch(Update_one_Sales_Action(id, {
        name:       formData.name.trim(),
        city:       formData.city,
        notes:      formData.note,
        userlog:    formData.userlog,
        teamleader: formData.teamleader,
        Manager:    formData.manager,
      }));
    } catch { Notify('error', 'Failed to update sales', 'error'); }
    finally   { setLoading(false); }
  };

  /* watch update result */
  const resSalesUpdate = useSelector(s => s.AllSales.salesupdate);
  useEffect(() => {
    if (!loading && resSalesUpdate?.status === 200 && dataReady) {
      Notify('success', 'Sales Updated Successfully', 'success');
      setTimeout(() => navigate('/sales'), 1500);
    }
  }, [loading]);

  /* ── styles ── */
  const fieldInput = {
    width: '100%', padding: '12px 14px', borderRadius: 10,
    border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
    background: isDark ? '#151827' : '#f8fafc',
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: 14, outline: 'none', fontFamily: "'DM Sans',sans-serif",
    boxSizing: 'border-box', transition: 'all 0.2s',
  };
  const cardStyle = {
    background: isDark ? '#1e2132' : '#fff',
    border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
    borderRadius: 14, padding: 24,
    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
  };
  const userRowStyle = sel => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px', cursor: 'pointer',
    background: sel ? (isDark ? '#1d2a5e' : '#eef1fb') : 'transparent',
    borderLeft: `3px solid ${sel ? '#0d2a6e' : 'transparent'}`,
    transition: 'background 0.12s',
  });

  return (
    <div style={{
      position: 'fixed', top: 50, left: sidebarWidth, right: 0, bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflowY: 'auto', overflowX: 'hidden',
      background: isDark ? 'linear-gradient(135deg,#0f1117,#1a1d2e)' : '#f0f2f5',
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>

      {/* Breadcrumb */}
      <div style={{ marginLeft: 10, marginTop: 30, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <Link to="/sales" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Sales</Link>
        <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
        <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Update Sales</span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 24, width: '100%', boxSizing: 'border-box' }}>

        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0d1b3e', margin: '0 0 4px', letterSpacing: '-0.3px' }}>Update Sales</h1>
          <p style={{ fontSize: 13, color: isDark ? '#6b7280' : '#94a3b8', margin: 0 }}>Edit sales member details, assigned cities and team structure</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: '100%' }}>

          {/* ── Name — full width ── */}
          <div style={{ ...cardStyle, gridColumn: '1 / span 2' }}>
            <SectionLabel icon={<IconNote />} label="Sales Name" required isDark={isDark} color="#0d2a6e" />
            <input
              style={fieldInput}
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              placeholder="Sales name…"
              onFocus={e => { e.target.style.borderColor = '#0d2a6e'; e.target.style.boxShadow = '0 0 0 3px rgba(13,42,110,0.1)'; }}
              onBlur={e  => { e.target.style.borderColor = isDark ? '#2d3248' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* ── Cities — full width ── */}
          <div style={{ ...cardStyle, gridColumn: '1 / span 2' }}>
            <SectionLabel icon={<IconCity />} label="Select Cities" required isDark={isDark} color="#0d2a6e" />

            {selectedCities.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {selectedCities.map(c => (
                  <span key={c._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: isDark ? '#1d2a5e' : '#eef1fb', color: isDark ? '#90b4ff' : '#0d2a6e', fontSize: 12, fontWeight: 500 }}>
                    {c.name}
                    <button onClick={() => toggleCity(c._id)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex', opacity: 0.7 }}><IconClose /></button>
                  </span>
                ))}
              </div>
            )}

            <Panel isDark={isDark} active={selectedCities.length > 0}>
              <div style={{ padding: 10, borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f0f0f0'}` }}>
                <SearchBox value={citySearch} onChange={setCitySearch} placeholder="Search cities…" isDark={isDark} />
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 8 }}>
                {filteredCities.length === 0 ? (
                  <div style={{ gridColumn: '1/span 2', padding: 20, textAlign: 'center', color: isDark ? '#6b7280' : '#9ca3af', fontSize: 13 }}>No cities found</div>
                ) : filteredCities.map(c => {
                  const sel = formData.city.includes(c._id);
                  return (
                    <div key={c._id} onClick={() => toggleCity(c._id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: sel ? (isDark ? '#1d2a5e' : '#eef1fb') : 'transparent', border: `1px solid ${sel ? '#0d2a6e' : 'transparent'}`, transition: 'all 0.12s' }}>
                      <span style={{ color: sel ? '#0d2a6e' : (isDark ? '#7070a0' : '#9ca3af'), display: 'flex' }}><IconCity /></span>
                      <span style={{ fontSize: 13, fontWeight: sel ? 600 : 400, color: sel ? (isDark ? '#90b4ff' : '#0d2a6e') : (isDark ? '#e0e0ff' : '#374151'), flex: 1 }}>{c.name}</span>
                      {sel && <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#0d2a6e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><IconCheck /></span>}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* ── Assign User — full width ── */}
          <div style={{ ...cardStyle, gridColumn: '1 / span 2' }}>
            <SectionLabel icon={<IconUser />} label="Assign User" required isDark={isDark} color="#0d2a6e" />

            {selectedUser && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 12, borderRadius: 10, background: isDark ? '#1d2a5e' : '#eef1fb', border: `1px solid ${isDark ? '#2d3a7e' : '#c7d2fe'}` }}>
                <Av name={selectedUser.name} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: isDark ? '#90b4ff' : '#0d2a6e' }}>{selectedUser.name}</div>
                  <div style={{ fontSize: 12, color: isDark ? '#7070a0' : '#6b7280' }}>{selectedUser.role} • {selectedUser.email}</div>
                </div>
                <button onClick={() => setFormData(p => ({ ...p, userlog: '' }))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#E53935', display: 'flex' }}><IconClose /></button>
              </div>
            )}

            <Panel isDark={isDark} active={!!selectedUser}>
              <div style={{ padding: 10, borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f0f0f0'}` }}>
                <SearchBox value={userSearch} onChange={setUserSearch} placeholder="Search users…" isDark={isDark} />
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {filteredUsers.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: isDark ? '#6b7280' : '#9ca3af', fontSize: 13 }}>No users found</div>
                ) : filteredUsers.map(u => {
                  const sel = formData.userlog === u._id;
                  return (
                    <div key={u._id} onClick={() => setFormData(p => ({ ...p, userlog: u._id }))}
                      style={userRowStyle(sel)}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? '#252545' : '#f5f7ff'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                      <Av name={u.name} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: sel ? (isDark ? '#90b4ff' : '#0d2a6e') : (isDark ? '#e0e0ff' : '#1f2937') }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: isDark ? '#7070a0' : '#9ca3af' }}>{u.role} • {u.email}</div>
                      </div>
                      {sel && <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#0d2a6e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><IconCheck /></span>}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* ── Team Leader ── */}
          <div style={cardStyle}>
            <SectionLabel icon={<IconTeam />} label="Team Leader" isDark={isDark} color="#16a34a" />

            {selectedTL && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 10, borderRadius: 10, background: isDark ? '#14301a' : '#f0fdf4', border: `1px solid ${isDark ? '#166534' : '#bbf7d0'}` }}>
                <Av name={selectedTL.name} size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#86efac' : '#15803d' }}>{selectedTL.name}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#4ade80' : '#16a34a', opacity: 0.8 }}>{selectedTL.role}</div>
                </div>
                <button onClick={() => setFormData(p => ({ ...p, teamleader: '' }))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#E53935', display: 'flex' }}><IconClose /></button>
              </div>
            )}

            <Panel isDark={isDark} active={!!selectedTL}>
              <div style={{ padding: 8, borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f0f0f0'}` }}>
                <SearchBox value={tlSearch} onChange={setTlSearch} placeholder="Search team leaders…" isDark={isDark} accentColor="#16a34a" />
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                <div onClick={() => setFormData(p => ({ ...p, teamleader: '' }))}
                  style={{ ...userRowStyle(false), color: isDark ? '#7070a0' : '#9ca3af', fontSize: 13 }}>None</div>
                {filteredTL.map(l => {
                  const sel = formData.teamleader === l._id;
                  return (
                    <div key={l._id} onClick={() => setFormData(p => ({ ...p, teamleader: l._id }))}
                      style={{ ...userRowStyle(sel), borderLeftColor: sel ? '#16a34a' : 'transparent' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? '#252545' : '#f0fdf4'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                      <Av name={l.name} size={30} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: sel ? (isDark ? '#86efac' : '#15803d') : (isDark ? '#e0e0ff' : '#1f2937') }}>{l.name}</div>
                        <div style={{ fontSize: 11, color: isDark ? '#7070a0' : '#9ca3af' }}>{l.email}</div>
                      </div>
                      {sel && <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><IconCheck /></span>}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* ── Manager ── */}
          <div style={cardStyle}>
            <SectionLabel icon={<IconManager />} label="Manager" isDark={isDark} color="#d97706" />

            {selectedMgr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 10, borderRadius: 10, background: isDark ? '#2d1a00' : '#fffbeb', border: `1px solid ${isDark ? '#92400e' : '#fde68a'}` }}>
                <Av name={selectedMgr.name} size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#fbbf24' : '#b45309' }}>{selectedMgr.name}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#d97706' : '#d97706', opacity: 0.8 }}>{selectedMgr.role}</div>
                </div>
                <button onClick={() => setFormData(p => ({ ...p, manager: '' }))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#E53935', display: 'flex' }}><IconClose /></button>
              </div>
            )}

            <Panel isDark={isDark} active={!!selectedMgr}>
              <div style={{ padding: 8, borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f0f0f0'}` }}>
                <SearchBox value={mgrSearch} onChange={setMgrSearch} placeholder="Search managers…" isDark={isDark} accentColor="#d97706" />
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                <div onClick={() => setFormData(p => ({ ...p, manager: '' }))}
                  style={{ ...userRowStyle(false), color: isDark ? '#7070a0' : '#9ca3af', fontSize: 13 }}>None</div>
                {filteredMgr.map(m => {
                  const sel = formData.manager === m._id;
                  return (
                    <div key={m._id} onClick={() => setFormData(p => ({ ...p, manager: m._id }))}
                      style={{ ...userRowStyle(sel), borderLeftColor: sel ? '#d97706' : 'transparent' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? '#252545' : '#fffbeb'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                      <Av name={m.name} size={30} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: sel ? (isDark ? '#fbbf24' : '#b45309') : (isDark ? '#e0e0ff' : '#1f2937') }}>{m.name}</div>
                        <div style={{ fontSize: 11, color: isDark ? '#7070a0' : '#9ca3af' }}>{m.email}</div>
                      </div>
                      {sel && <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><IconCheck /></span>}
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          {/* ── Notes — full width ── */}
          <div style={{ ...cardStyle, gridColumn: '1 / span 2' }}>
            <SectionLabel icon={<IconNote />} label="Additional Notes" required isDark={isDark} color="#0d2a6e" />
            <textarea
              value={formData.note}
              onChange={e => setFormData(p => ({ ...p, note: e.target.value }))}
              placeholder="Add notes here…"
              rows={4}
              style={{ ...fieldInput, resize: 'vertical', minHeight: 100 }}
              onFocus={e => { e.target.style.borderColor = '#0d2a6e'; e.target.style.boxShadow = '0 0 0 3px rgba(13,42,110,0.1)'; }}
              onBlur={e  => { e.target.style.borderColor = isDark ? '#2d3248' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* ── Buttons — full width ── */}
          <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => navigate('/sales')}
              style={{ padding: '13px 28px', borderRadius: 10, border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#9ca3af' : '#64748b', fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'all 0.2s' }}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.name || !formData.city.length || !formData.userlog}
              style={{ padding: '13px 32px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: (loading || !formData.name || !formData.city.length || !formData.userlog) ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 16px rgba(29,78,216,0.35)', transition: 'all 0.2s', opacity: (loading || !formData.name || !formData.city.length || !formData.userlog) ? 0.6 : 1 }}>
              {loading
                ? <><CircularProgress size={15} style={{ color: '#fff' }} /><span>Updating…</span></>
                : <><IconEdit /><span>Update Sales</span></>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};
